/**
 * AB#2201 — where to send an owner after they sign in again.
 *
 * When a session expires the portal sends the owner to
 * `/form/login?returnTo=<the page they were on>`; after a successful login they land
 * back on that page instead of the dashboard. Only same-origin, in-app paths are
 * honoured (never `//evil.com`, absolute URLs, control-character tricks or the auth
 * pages themselves), so the parameter cannot be used as an open redirect or to break
 * the post-login navigation.
 */

const MAX_LENGTH = 512;
const ORIGIN = 'https://portal.invalid';

/** A safe in-app path (pathname + search + hash), or null when `raw` must not be followed. */
export function safeReturnPath(raw: string | null | undefined): string | null {
    if (!raw) return null;
    if (raw.length > MAX_LENGTH) return null;
    // Control characters and backslashes are normalised away by URL parsers
    // ("/\t/evil" → "//evil"): refuse them outright.
    // eslint-disable-next-line no-control-regex
    if (/[\u0000-\u001f\u007f\\]/.test(raw)) return null;
    if (!raw.startsWith('/') || raw.startsWith('//')) return null;
    let url: URL;
    try {
        url = new URL(raw, ORIGIN);
    } catch {
        return null;
    }
    if (url.origin !== ORIGIN) return null;
    const path = `${url.pathname}${url.search}${url.hash}`;
    const lower = url.pathname.toLowerCase();
    if (lower === '/form' || lower.startsWith('/form/')) return null;
    return path;
}

/** `/form/login`, carrying the current page as `returnTo` when it is worth returning to. */
export function loginPathFor(currentPath: string): string {
    const back = safeReturnPath(currentPath);
    return back && back !== '/' ? `/form/login?returnTo=${encodeURIComponent(back)}` : '/form/login';
}

/** The page to open after a successful login: the safe `returnTo`, else the dashboard. */
export function postLoginPath(search: string = typeof window !== 'undefined' ? window.location.search : ''): string {
    const raw = new URLSearchParams(search).get('returnTo');
    return safeReturnPath(raw) ?? '/';
}

/** True while the owner is on an auth page (/form/…). */
export function isOnAuthPage(pathname: string = typeof window !== 'undefined' ? window.location.pathname : ''): boolean {
    const lower = pathname.toLowerCase();
    return lower === '/form' || lower.startsWith('/form/');
}
